/**
 * DashboardCockpit - Car-dashboard style overview.
 *
 * Uses SVG gauges (arc + needle) to visualize the 3 main KPIs of the agency:
 *  - Center (speedometer): monthly collection rate (%)
 *  - Left gauge : occupancy rate (%)
 *  - Right gauge: on-time payment rate (%)
 *
 * Below the gauges: warning lights (indicators) driven by action-center counters.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle, Wrench, FileSignature, MapPin,
  Fuel, Gauge as GaugeIcon, Home, Activity, Timer,
} from 'lucide-react';
import { useActionCounters } from '../context/ActionCountersContext';
import api from '../lib/api';

// ---------- SVG Gauge (arc + needle) ----------
export const Gauge = ({ value = 0, max = 100, label, sub, size = 180, thickness = 14, color = '#D4A745', dangerBelow, warningBelow, startupDelay = 0 }) => {
  // Startup sweep animation: needle boots at 0 then swings to value on mount.
  const [displayValue, setDisplayValue] = React.useState(0);
  React.useEffect(() => {
    // Sweep first to slightly-above-max (like a car dashboard test at ignition),
    // then land on the real value. Feels "alive".
    const overshoot = setTimeout(() => setDisplayValue(max), startupDelay);
    const settle = setTimeout(() => setDisplayValue(value), startupDelay + 900);
    return () => { clearTimeout(overshoot); clearTimeout(settle); };
  }, [value, max, startupDelay]);

  const v = Math.max(0, Math.min(max, Number(displayValue) || 0));
  const pct = v / max;
  // Arc geometry: half-circle from left (180°) to right (0°)
  const cx = size / 2, cy = size * 0.72, r = size / 2 - thickness / 2 - 4;
  const startAngle = Math.PI, endAngle = 0;
  const angle = startAngle + (endAngle - startAngle) * pct; // in radians
  const arcLen = Math.PI * r; // half circle length
  const dash = arcLen * pct;
  const gap = arcLen - dash;
  // Needle position
  const nx = cx + Math.cos(angle) * (r - 4);
  const ny = cy + Math.sin(angle) * (r - 4);
  // Determine dynamic color based on the FINAL target value (not the animated one)
  let arcColor = color;
  const targetV = Math.max(0, Math.min(max, Number(value) || 0));
  if (dangerBelow != null && targetV < dangerBelow) arcColor = '#EF4444';
  else if (warningBelow != null && targetV < warningBelow) arcColor = '#F59E0B';

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size * 0.85} viewBox={`0 0 ${size} ${size * 0.85}`} className="drop-shadow">
        {/* Background arc */}
        <path
          d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          fill="none" stroke="#1F2937" strokeWidth={thickness} strokeLinecap="round"
        />
        {/* Value arc */}
        <path
          d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          fill="none" stroke={arcColor} strokeWidth={thickness} strokeLinecap="round"
          strokeDasharray={`${dash} ${gap}`}
          style={{ transition: 'stroke-dasharray 900ms cubic-bezier(.22,1.2,.36,1), stroke 400ms ease' }}
        />
        {/* Tick marks (5 major, 4 minor) */}
        {[0, 0.25, 0.5, 0.75, 1].map((t, i) => {
          const a = startAngle + (endAngle - startAngle) * t;
          const x1 = cx + Math.cos(a) * (r - thickness);
          const y1 = cy + Math.sin(a) * (r - thickness);
          const x2 = cx + Math.cos(a) * (r + 2);
          const y2 = cy + Math.sin(a) * (r + 2);
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#4B5563" strokeWidth={1.5} />;
        })}
        {/* Needle */}
        <line x1={cx} y1={cy} x2={nx} y2={ny} stroke={arcColor} strokeWidth={3} strokeLinecap="round"
              style={{ transition: 'x2 900ms cubic-bezier(.22,1.2,.36,1), y2 900ms cubic-bezier(.22,1.2,.36,1), stroke 400ms ease' }} />
        <circle cx={cx} cy={cy} r={6} fill="#1F2937" stroke={arcColor} strokeWidth={2} />
        {/* Value in center */}
        <text x={cx} y={cy - r * 0.35} textAnchor="middle" fill="#F3F4F6" fontSize={size * 0.22} fontWeight="bold" fontFamily="'JetBrains Mono', monospace"
              style={{ transition: 'fill 400ms ease' }}>
          {Math.round(v)}
        </text>
        <text x={cx} y={cy - r * 0.15} textAnchor="middle" fill="#9CA3AF" fontSize={size * 0.08} fontFamily="'Inter', sans-serif">
          {max === 100 ? '%' : ''}
        </text>
      </svg>
      <div className="text-center -mt-2">
        <p className="text-[11px] uppercase tracking-widest text-gray-400 font-bold">{label}</p>
        {sub && <p className="text-[10px] text-gray-500 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
};

// ---------- Warning light (indicator) ----------
const WarningLight = ({ icon: Icon, label, count, active, to, color = 'amber' }) => {
  const colorMap = {
    amber: active ? 'bg-amber-500/20 border-amber-400 text-amber-300' : 'bg-gray-800/40 border-gray-700 text-gray-500',
    red: active ? 'bg-red-500/20 border-red-400 text-red-300' : 'bg-gray-800/40 border-gray-700 text-gray-500',
    blue: active ? 'bg-blue-500/20 border-blue-400 text-blue-300' : 'bg-gray-800/40 border-gray-700 text-gray-500',
    emerald: active ? 'bg-emerald-500/20 border-emerald-400 text-emerald-300' : 'bg-gray-800/40 border-gray-700 text-gray-500',
  };
  const cls = colorMap[color];
  const body = (
    <div className={`relative flex flex-col items-center gap-1 p-3 rounded-xl border-2 ${cls} transition-all ${active ? 'animate-pulse' : ''}`}>
      <Icon className="w-6 h-6" />
      <span className="text-[10px] uppercase tracking-wider font-bold text-center leading-tight">{label}</span>
      {active && count > 0 && (
        <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-lg">
          {count > 99 ? '99+' : count}
        </span>
      )}
    </div>
  );
  return active && to ? <Link to={to}>{body}</Link> : body;
};

const formatMoney = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1).replace('.0', '') + 'M';
  if (v >= 1000) return Math.round(v / 1000) + 'k';
  return String(Math.round(v));
};

/**
 * @param {Object} props.stats - the object returned by GET /api/dashboard/stats
 */
const DashboardCockpit = ({ stats }) => {
  const { perRoute, items } = useActionCounters();

  // Processing-times metric (delai de traitement des dossiers)
  const [processing, setProcessing] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/dashboard/processing-times');
        if (!cancelled) setProcessing(r.data);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const collection = Number(stats?.collection_rate_percentage) || 0;
  const occupancy = Number(stats?.occupancy_percentage) || 0;
  const onTime = Number(stats?.payment_on_time_percentage) || 0;

  // Warning-light counts from the ActionCounters context
  const findCount = (key) => items.find(i => i.key === key)?.count || 0;
  const overdue = findCount('overdue_notices');
  const missingNotices = findCount('missing_notices');
  const unsigned = findCount('unsigned_contracts');
  const repair = findCount('repair_open');
  const parcel = findCount('parcel_open');
  const ending = findCount('ending_soon');

  return (
    <div
      className="relative rounded-3xl overflow-hidden shadow-2xl"
      style={{
        background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 40%, #0B1120 100%)',
      }}
      data-testid="dashboard-cockpit"
    >
      {/* Subtle grid overlay for the "carbon" feel */}
      <div
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      />
      {/* Top strip: brand + odometer */}
      <div className="relative flex items-center justify-between px-6 py-3 border-b border-white/10">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-emerald-400 animate-pulse" />
          <span className="text-[10px] uppercase tracking-[0.25em] text-gray-400 font-semibold">
            Cockpit · Temps réel
          </span>
        </div>
        <div className="flex items-center gap-3 font-mono text-xs">
          <span className="text-gray-500">TOTAL PERÇU</span>
          <span className="text-emerald-300 font-bold tabular-nums bg-black/40 px-2 py-0.5 rounded border border-emerald-800/50">
            {formatMoney(stats?.total_rent_collected)} XOF
          </span>
        </div>
      </div>

      {/* Gauges row */}
      <div className="relative grid grid-cols-3 gap-2 px-2 py-6 sm:px-6">
        <Gauge
          value={occupancy}
          label="Taux d'occupation"
          sub={`${stats?.occupied_units || 0}/${stats?.total_units || 0} unités`}
          size={170}
          color="#22C55E"
          dangerBelow={40}
          warningBelow={70}
          startupDelay={80}
        />
        <div className="scale-110">
          <Gauge
            value={collection}
            label="Encaissement du mois"
            sub={`${formatMoney(stats?.monthly_collected_amount)} / ${formatMoney(stats?.monthly_expected_amount)} XOF`}
            size={200}
            color="#D4A745"
            dangerBelow={40}
            warningBelow={70}
            startupDelay={220}
          />
        </div>
        <Gauge
          value={onTime}
          label="Paiements à l'heure"
          sub={`${stats?.monthly_on_time_count || 0}/${stats?.monthly_due_count || 0} du mois`}
          size={170}
          color="#3B82F6"
          dangerBelow={40}
          warningBelow={70}
          startupDelay={360}
        />
      </div>

      {/* Processing-time gauge (delai de traitement des dossiers) */}
      <div className="relative border-t border-white/10 px-4 sm:px-6 py-4">
        <div className="flex flex-col sm:flex-row items-stretch gap-4">
          {/* Horizontal linear gauge */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Timer className="w-4 h-4 text-purple-400" />
                <p className="text-[11px] uppercase tracking-widest text-gray-300 font-bold">
                  Délai moyen de traitement
                </p>
              </div>
              {(() => {
                const d = Number(processing?.overall_avg_days) || 0;
                const color = d === 0
                  ? 'text-gray-400'
                  : d <= 3 ? 'text-emerald-300'
                  : d <= 7 ? 'text-amber-300'
                  : 'text-red-300';
                return (
                  <div className={`font-mono font-bold ${color} text-lg tabular-nums`} data-testid="processing-avg-days">
                    {d.toFixed(1)}<span className="text-xs font-normal ml-1">jours</span>
                  </div>
                );
              })()}
            </div>
            {/* Bar visualization (0..15 days scale) */}
            <div className="relative h-4 bg-gray-800/80 rounded-full overflow-hidden border border-white/5">
              {/* Segmented background zones */}
              <div className="absolute inset-y-0 left-0 w-1/5 bg-emerald-500/20" />
              <div className="absolute inset-y-0 left-1/5 w-1/5 bg-emerald-500/10" />
              <div className="absolute inset-y-0 left-2/5 w-1/5 bg-amber-500/15" />
              <div className="absolute inset-y-0 left-3/5 w-1/5 bg-red-500/15" />
              <div className="absolute inset-y-0 left-4/5 w-1/5 bg-red-500/25" />
              {/* Fill bar */}
              {(() => {
                const d = Number(processing?.overall_avg_days) || 0;
                const max = 15;
                const pct = Math.min(1, d / max) * 100;
                const color = d <= 3 ? '#22C55E' : d <= 7 ? '#F59E0B' : '#EF4444';
                return (
                  <div
                    className="absolute inset-y-0 left-0 rounded-full"
                    style={{
                      width: `${pct}%`,
                      backgroundColor: color,
                      transition: 'width 900ms cubic-bezier(.22,1.2,.36,1), background-color 400ms ease',
                      boxShadow: `0 0 10px ${color}`,
                    }}
                  />
                );
              })()}
              {/* Tick marks at 3, 7, 15 */}
              {[3, 7, 15].map((t) => (
                <div
                  key={t}
                  className="absolute inset-y-0 w-px bg-white/30"
                  style={{ left: `${(t / 15) * 100}%` }}
                />
              ))}
            </div>
            <div className="flex justify-between mt-1 text-[9px] font-mono text-gray-500">
              <span>0j</span><span>3j</span><span>7j</span><span>15j+</span>
            </div>
          </div>

          {/* Breakdown per dossier type */}
          <div className="flex sm:flex-col gap-2 sm:min-w-[220px]">
            {(processing?.breakdown || []).map((b) => {
              const d = Number(b.avg_days) || 0;
              const color = b.count === 0
                ? 'bg-gray-800/40 border-gray-700 text-gray-500'
                : d <= 3 ? 'bg-emerald-500/10 border-emerald-600/40 text-emerald-300'
                : d <= 7 ? 'bg-amber-500/10 border-amber-600/40 text-amber-300'
                : 'bg-red-500/10 border-red-600/40 text-red-300';
              return (
                <div
                  key={b.key}
                  className={`flex-1 sm:flex-none flex items-center justify-between rounded-lg border px-2.5 py-1.5 ${color}`}
                  data-testid={`processing-breakdown-${b.key}`}
                >
                  <span className="text-[10px] uppercase font-semibold truncate">{b.label}</span>
                  <span className="font-mono font-bold text-xs tabular-nums ml-2">
                    {b.count === 0 ? '—' : `${d.toFixed(1)}j`}
                    <span className="text-[9px] opacity-60 ml-1">({b.count})</span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        {processing?.total_closed !== undefined && (
          <p className="text-[9px] font-mono text-gray-500 mt-2 text-right">
            {processing.total_closed} dossier{processing.total_closed > 1 ? 's' : ''} clos sur {processing.window_days || 90} derniers jours
          </p>
        )}
      </div>

      {/* Warning lights */}
      <div className="relative border-t border-white/10 px-4 py-4">
        <p className="text-[10px] uppercase tracking-widest text-gray-400 font-bold mb-3 px-2">
          Voyants d&apos;alerte
        </p>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          <WarningLight icon={AlertTriangle} label="Retards" count={overdue} active={overdue > 0} to="/due-notices" color="red" />
          <WarningLight icon={Fuel} label="Avis à générer" count={missingNotices} active={missingNotices > 0} to="/due-notices" color="amber" />
          <WarningLight icon={FileSignature} label="Contrats" count={unsigned} active={unsigned > 0} to="/transactions" color="blue" />
          <WarningLight icon={Wrench} label="Réparations" count={repair} active={repair > 0} to="/repair-requests" color="amber" />
          <WarningLight icon={MapPin} label="Parcelles" count={parcel} active={parcel > 0} to="/parcel-verifications" color="blue" />
          <WarningLight icon={Home} label="Baux fin proche" count={ending} active={ending > 0} to="/transactions" color="emerald" />
        </div>
      </div>

      {/* Bottom digital odometer strip */}
      <div className="relative bg-black/40 border-t border-white/10 px-6 py-2 flex items-center justify-between text-[11px] font-mono">
        <div className="flex items-center gap-4 text-gray-400">
          <span>BIENS: <span className="text-white font-bold">{stats?.total_properties || 0}</span></span>
          <span>CLIENTS: <span className="text-white font-bold">{stats?.total_clients || 0}</span></span>
          <span>BAUX: <span className="text-white font-bold">{stats?.total_transactions || 0}</span></span>
        </div>
        <div className="flex items-center gap-2 text-gray-400">
          <GaugeIcon className="w-3.5 h-3.5" />
          <span>REV. TOTAL: <span className="text-emerald-300 font-bold">{formatMoney(stats?.total_revenue)} XOF</span></span>
        </div>
      </div>
    </div>
  );
};

export default DashboardCockpit;
